import type { Entity } from '@backstage/catalog-model';
import {
  Boxes,
  FileText,
  GitPullRequestArrow,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Workflow,
  type LucideProps,
} from 'lucide-react';
import type { ComponentType } from 'react';

/** Radix single-select ToggleGroup needs a value for the "no category" option. */
export const ALL_CATEGORIES = '__all__';

/** Shown wherever templates are offered to someone without `taskCreatePermission`. */
export const NO_RUN_PERMISSION = {
  title: "You can't run templates",
  description:
    "Templates change things in GitHub and Azure, so they are limited to members of the portal's writer group. Ask the platform team for access.",
} as const;

export const UNCATEGORISED = 'other';

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

export const templateCategory = (template: Entity): string =>
  asString(template.spec?.type) ?? UNCATEGORISED;

export const templateOwner = (template: Entity): string | undefined =>
  asString(template.spec?.owner)?.replace(/^[^:]+:/, '');

export const templateTitle = (template: Entity): string =>
  template.metadata.title ?? template.metadata.name;

export const templateTags = (template: Entity): string[] =>
  template.metadata.tags ?? [];

type CategoryVisual = {
  icon: ComponentType<LucideProps>;
  /** Decorative only — a tint on the card's top rule, never behind text. */
  accent: string;
};

const CATEGORY_VISUALS: Record<string, CategoryVisual> = {
  compliance: { icon: ShieldCheck, accent: 'bg-panel-brand' },
  infrastructure: { icon: ServerCog, accent: 'bg-panel-info' },
  documentation: { icon: FileText, accent: 'bg-panel-insight' },
  service: { icon: Boxes, accent: 'bg-panel-attention' },
  website: { icon: Workflow, accent: 'bg-panel-info' },
  automation: { icon: GitPullRequestArrow, accent: 'bg-panel-attention' },
};

const FALLBACK_VISUAL: CategoryVisual = {
  icon: Sparkles,
  accent: 'bg-panel-brand',
};

export const categoryVisual = (category: string): CategoryVisual =>
  CATEGORY_VISUALS[category] ?? FALLBACK_VISUAL;

/** `[value, count]` pairs, most populated first, then alphabetical. */
export const countBy = (values: string[]): [string, number][] => {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()].sort(
    ([aValue, aCount], [bValue, bCount]) =>
      bCount - aCount || aValue.localeCompare(bValue),
  );
};

export const matchesQuery = (template: Entity, query: string): boolean => {
  if (!query) return true;
  return [
    templateTitle(template),
    template.metadata.name,
    template.metadata.description ?? '',
    templateCategory(template),
    templateOwner(template) ?? '',
    ...templateTags(template),
  ]
    .join(' ')
    .toLowerCase()
    .includes(query);
};
